import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useGetCase } from '../../../../../../../api/queries/useGetCase';
import { SubsetChip } from '../../../../../../components/subset-chip';
import { ScrollableTable } from '../../../../../../components/table/scrollable-table';
import { formatDatetimeForTable } from '../../../../../../lib/table-utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteInconsistencyReport } from '@/api/mutations/useDeleteInconsistencyReport';
import { useGetInconsistencyReports } from '@/api/queries/useGetInconsistencyReports';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { InconsistencyReport } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

const fallbackTableData: InconsistencyReport[] = [];

const columnHelper = createColumnHelper<InconsistencyReport>();

function ActionsCell({ report, caseId }: { report: InconsistencyReport; caseId: string }) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useUserContext();

  const { mutate: deleteReport, isLoading: isDeleting } = useDeleteInconsistencyReport({
    reportId: report.id,
    caseId,
  });

  const handleDeleteReport = async () => {
    deleteReport();
    trackCustomEvent(TrackingEventsV2.DeleteInconsistencyReport, { reportId: report.id });
    setDeleteModalOpen(false);
  };

  const canDelete = user?.id === report.createdById;

  const handleDeleteClick = () => {
    if (canDelete) {
      setDeleteModalOpen(true);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={canDelete ? 'destructive' : 'default'}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }}
          className="-my-1"
          disabled={!canDelete}
        >
          Delete
        </Button>
      </div>

      <ConfirmationModal
        onOpenChange={setDeleteModalOpen}
        isOpen={deleteModalOpen}
        title="Delete inconsistency report"
        onConfirm={handleDeleteReport}
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete this inconsistency report?</p>
      </ConfirmationModal>
    </>
  );
}

const columns = [
  columnHelper.accessor('title', {
    header: () => <span>Title</span>,
    cell: (info) => <Link to={info.row.original.id}>{info.getValue()}</Link>,
  }),
  columnHelper.accessor('createdDate', {
    header: () => <span>Date Created</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <span>Actions</span>,
    cell: (info) => <ActionsCell report={info.row.original} caseId={info.row.original.caseId} />,
  }),
];

export function Reports() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const navigate = useNavigate();

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetCase(subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Inconsistency Reports', path: 'reports' },
  });

  const { data: reports, isLoading } = useGetInconsistencyReports({
    caseId: subsetId,
    paginated: false,
  });

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: reports?.data ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageContainer>
        <PageHeader>
          <div className="flex items-center gap-4">
            <PageTitle text="Inconsistency Reports" />
            <SubsetChip subsetId={subsetId} className="mt-0.5" />
          </div>
        </PageHeader>
      </PageContainer>

      <ScrollableTable
        table={table}
        plural="reports"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        rowOnClick={(row) => navigate(row.original.id)}
        loading={isLoading}
      />
    </>
  );
}
